import { HttpClient } from '@angular/common/http'
import { Inject, Injectable, NgZone } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { ClientSurfaceEnum, ENV, Environment, currentColorTheme } from 'lib-trend-core'
import {
  CHAT_APP_OPEN_ACK,
  CHAT_APP_OPEN_MESSAGE,
  CHAT_APP_WINDOW_NAME,
  ChatAppTarget,
  buildChatAppEntryUrl,
  buildChatAppHandoffUrl,
  buildChatAppPath,
  chatAppOrigin,
} from 'lib-trend-core/chat'
import Swal from 'sweetalert2'

/** How long an open chat app window has to acknowledge before it is navigated instead. */
const OPEN_ACK_TIMEOUT_MS = 2500

/** Retry cadence of the open message while the window has not acknowledged. */
const OPEN_RETRY_INTERVAL_MS = 250

/** `POST /api/auth/handoff` success envelope. */
interface ChatHandoffMintResponse {
  data?: { code?: string; expiresAt?: string }
}

/**
 * Opens the standalone chat app from the product — SPEC-2026-09-internal-chat-pwa
 * (T-40 · RF-05 · RF-11 · RF-12 · RF-13).
 *
 * 1. The named window is opened INSIDE the click (a pop-up opened after an await is
 *    blocked). An existing chat app window with that name is returned instead of a new
 *    one.
 * 2. A one-time sign-in code is minted for the current product session.
 * 3. A fresh window is sent to the handoff landing (code in the fragment); an existing
 *    chat app window is told where to go through the origin-checked message protocol and
 *    focused — the conversation opens in place, no reload. A window that does not answer
 *    in time is navigated to the handoff landing instead.
 *
 * A failed mint still lands on the chat app, which asks for sign-in. A blocked pop-up
 * shows a localized message with a direct entry.
 */
@Injectable({ providedIn: 'root' })
export class ChatAppLauncherService {

  constructor(
    private readonly http: HttpClient,
    private readonly zone: NgZone,
    private readonly translate: TranslateService,
    @Inject(ENV) private readonly environment: Environment,
  ) { }

  private get chatAppUrl(): string {
    return String(this.environment['trendWebChatUrl'] ?? '')
  }

  /** Must be called synchronously from the user's click. */
  open(target: ChatAppTarget = {}): void {
    const entryUrl = buildChatAppEntryUrl(this.chatAppUrl, target)
    if (!entryUrl) {
      console.warn('ChatAppLauncherService - trendWebChatUrl is not configured')
      return
    }

    let chatWindow: Window | null = null
    try {
      chatWindow = window.open('', CHAT_APP_WINDOW_NAME)
    } catch {
      chatWindow = null
    }
    if (!chatWindow) {
      this.showBlockedPopup(entryUrl)
      return
    }

    const fresh = this.isBlankWindow(chatWindow)
    if (fresh) {
      try {
        // Nothing to show yet: never leave a blank window without a word.
        chatWindow.document.title = 'Trendmulti Chat'
      } catch {
        /* not accessible — harmless */
      }
    }

    this.mintCode().then((code) => {
      if (fresh) {
        this.navigateWindow(chatWindow!, code ? buildChatAppHandoffUrl(this.chatAppUrl, target, code, currentColorTheme()) : entryUrl)
        return
      }
      this.postOpen(chatWindow!, target, code)
    })
  }

  /** Same-origin `about:blank` = a window we just created; a chat app window is cross-origin. */
  private isBlankWindow(chatWindow: Window): boolean {
    try {
      return chatWindow.location.href === 'about:blank'
    } catch {
      return false
    }
  }

  private mintCode(): Promise<string | null> {
    return new Promise((resolve) => {
      this.http.post<ChatHandoffMintResponse>('/auth/handoff', { target: ClientSurfaceEnum.WEB_CHAT }).subscribe({
        next: (response) => resolve(response?.data?.code ?? null),
        error: () => resolve(null),
      })
    })
  }

  private navigateWindow(chatWindow: Window, url: string | null): void {
    if (!url) return
    try {
      chatWindow.location.replace(url)
      chatWindow.focus()
    } catch {
      /* the window was closed meanwhile */
    }
  }

  /**
   * Existing chat app window: tell it where to go, retrying until it acknowledges. The
   * target origin is pinned, so nothing is ever posted to a page of another origin.
   */
  private postOpen(chatWindow: Window, target: ChatAppTarget, code: string | null): void {
    const origin = chatAppOrigin(this.chatAppUrl)
    if (!origin) return

    const requestId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    const message = { type: CHAT_APP_OPEN_MESSAGE, requestId, path: buildChatAppPath(target), ...(code ? { code } : {}) }
    let acknowledged = false

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return
      if (event.data?.type === CHAT_APP_OPEN_ACK && event.data?.requestId === requestId) {
        acknowledged = true
      }
    }
    window.addEventListener('message', onMessage)

    this.zone.runOutsideAngular(() => {
      const startedAt = Date.now()
      const tick = () => {
        if (acknowledged) {
          window.removeEventListener('message', onMessage)
          return
        }
        if (Date.now() - startedAt >= OPEN_ACK_TIMEOUT_MS) {
          window.removeEventListener('message', onMessage)
          // A chat app page still loading, or not the app at all: navigate it.
          const url = code ? buildChatAppHandoffUrl(this.chatAppUrl, target, code, currentColorTheme()) : buildChatAppEntryUrl(this.chatAppUrl, target)
          this.zone.run(() => this.navigateWindow(chatWindow, url))
          return
        }
        try {
          chatWindow.postMessage(message, origin)
        } catch {
          /* closed */
        }
        setTimeout(tick, OPEN_RETRY_INTERVAL_MS)
      }
      tick()
    })

    try {
      chatWindow.focus()
    } catch {
      /* closed */
    }
  }

  private showBlockedPopup(entryUrl: string): void {
    Swal.fire({
      icon: 'info',
      title: this.translate.instant('chat.launcher.blockedTitle'),
      text: this.translate.instant('chat.launcher.blockedMessage'),
      showCancelButton: true,
      confirmButtonText: this.translate.instant('chat.launcher.openDirect'),
      cancelButtonText: this.translate.instant('chat.launcher.close'),
    }).then((result) => {
      if (result.isConfirmed) {
        // A click on the dialog's button is a fresh user gesture: this open is allowed.
        window.open(entryUrl, CHAT_APP_WINDOW_NAME)
      }
    })
  }
}
